import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Modal, Button } from 'react-bootstrap';
import CampaignAudienceSelector from './CampaignAudienceSelector';
import AudiencePreviewPanel from './AudiencePreviewPanel';
import RichTextEditor from './RichTextEditor';
import { CampaignStatusBadge, CampaignTypeBadge } from './CampaignStatusBadge';
import {
    AUDIENCE_REQUIRED_MESSAGE,
    EMPTY_AUDIENCE,
    SCHEDULE_FUTURE_MESSAGE,
    SCHEDULE_REQUIRED_MESSAGE,
    audienceSummaryLabels,
    campaignHasAudience,
    formatCampaignSaveError,
    isScheduleInFuture,
    scheduleInOneMinute,
    toDatetimeLocalValue,
} from './campaignFormUtils';

const STEPS = ['Setup', 'Content', 'Review'];

function StepIndicator({ current }) {
    return (
        <div className="d-flex align-items-center gap-0 mb-4">
            {STEPS.map((label, i) => {
                const done = i < current;
                const active = i === current;
                return (
                    <React.Fragment key={i}>
                        <div className="text-center" style={{ minWidth: 72 }}>
                            <div
                                className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-1 ${done ? 'bg-success' : active ? 'bg-primary' : 'bg-light border'}`}
                                style={{ width: 28, height: 28, fontSize: 12, color: done || active ? '#fff' : '#6c757d' }}
                            >
                                {done ? '✓' : i + 1}
                            </div>
                            <div className={`small ${active ? 'fw-semibold' : 'text-muted'}`} style={{ fontSize: '0.7rem' }}>{label}</div>
                        </div>
                        {i < STEPS.length - 1 && (
                            <div className="flex-grow-1" style={{ height: 2, background: done ? '#198754' : '#dee2e6', marginBottom: '1.2rem' }} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

export default function CampaignWizard({
    mode,
    campaignId: initialCampaignId,
    campaign,
    leagueId,
    leagueName,
}) {
    const navigate = useNavigate();
    const isEdit = mode === 'edit';
    const [step, setStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedFlash, setSavedFlash] = useState(false);
    const [campaignId, setCampaignId] = useState(initialCampaignId || null);
    const [fromEmail, setFromEmail] = useState(campaign?.from_email || '');
    const [showSendNow, setShowSendNow] = useState(false);
    const [initialized, setInitialized] = useState(!isEdit);

    const [setup, setSetup] = useState({
        title: campaign?.title || '',
        campaign_type: campaign?.campaign_type || 'operational',
    });
    const [audience, setAudience] = useState(EMPTY_AUDIENCE);
    const [settings, setSettings] = useState({
        scheduled_for: '',
        batch_size: 25,
        send_rate_per_minute: 25,
        max_recipient_attempts: 3,
        status: 'draft',
    });
    const [content, setContent] = useState({
        subject: '',
        preheader: '',
        html_body: '',
        plain_text_body: '',
        plain_text_custom: false,
    });

    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const debounceRef = useRef(null);
    const [emailPreview, setEmailPreview] = useState({ mode: false, html: '', width: 'desktop', loading: false });

    useEffect(() => {
        if (!isEdit || !campaign || initialized) return;
        setSetup({ title: campaign.title || '', campaign_type: campaign.campaign_type || 'operational' });
        setAudience({
            include_league_players: campaign.include_league_players || false,
            include_season_players: campaign.include_season_players || false,
            include_team_players: campaign.include_team_players || false,
            include_selected_players: campaign.include_selected_players || false,
            include_subscribers: campaign.include_subscribers || false,
            include_previous_players: campaign.include_previous_players || false,
            season_ids: campaign.season_ids || [],
            team_ids: campaign.team_ids || [],
            selected_player_ids: campaign.selected_player_ids || [],
            selected_player_names: [],
            selected_email_list_ids: campaign.selected_email_list_ids || [],
            manually_excluded_emails: campaign.manually_excluded_emails || [],
        });
        setContent({
            subject: campaign.subject || '',
            preheader: campaign.preheader || '',
            html_body: campaign.html_body || '',
            plain_text_body: campaign.plain_text_body || '',
            plain_text_custom: !!(campaign.plain_text_body),
        });
        setSettings({
            scheduled_for: campaign.scheduled_for ? toDatetimeLocalValue(campaign.scheduled_for) : '',
            batch_size: campaign.batch_size || 25,
            send_rate_per_minute: campaign.send_rate_per_minute || 25,
            max_recipient_attempts: campaign.max_recipient_attempts || 3,
            status: campaign.status || 'draft',
        });
        setFromEmail(campaign.from_email || '');
        setCampaignId(campaign.id || initialCampaignId);
        setInitialized(true);
    }, [campaign, initialized, isEdit, initialCampaignId]);

    const isLocked = settings.status === 'sending' || settings.status === 'sent';
    const recipientNet = previewData
        ? (previewData.summary?.included || 0) - (audience.manually_excluded_emails?.length || 0)
        : null;

    const buildPayload = useCallback((overrides = {}) => ({
        title: setup.title,
        campaign_type: setup.campaign_type,
        league_id: leagueId,
        subject: content.subject,
        preheader: content.preheader,
        html_body: content.html_body,
        plain_text_body: content.plain_text_custom ? content.plain_text_body : '',
        include_league_players: audience.include_league_players,
        include_season_players: audience.include_season_players,
        include_team_players: audience.include_team_players,
        include_selected_players: audience.include_selected_players,
        include_subscribers: audience.include_subscribers,
        include_previous_players: audience.include_previous_players,
        season_ids: audience.season_ids,
        team_ids: audience.team_ids,
        selected_player_ids: audience.selected_player_ids,
        selected_email_list_ids: audience.selected_email_list_ids,
        manually_excluded_emails: audience.manually_excluded_emails,
        scheduled_for: settings.scheduled_for || null,
        batch_size: settings.batch_size,
        send_rate_per_minute: settings.send_rate_per_minute,
        max_recipient_attempts: settings.max_recipient_attempts,
        status: settings.status === 'ready' ? 'ready' : 'draft',
        ...overrides,
    }), [setup, audience, content, settings, leagueId]);

    const saveProgress = useCallback(async (overrides = {}) => {
        setSaving(true);
        setError(null);
        try {
            const payload = buildPayload(overrides);
            let data;
            if (campaignId) {
                const res = await axios.patch(`/api/campaigns/${campaignId}/`, payload, { withCredentials: true });
                data = res.data;
            } else {
                const res = await axios.post('/api/campaigns/', payload, { withCredentials: true });
                data = res.data;
                setCampaignId(data.id);
            }
            if (data?.from_email) setFromEmail(data.from_email);
            if (data?.status) {
                setSettings((prev) => ({ ...prev, status: data.status }));
            }
            return data.id;
        } catch (err) {
            setError(formatCampaignSaveError(err));
            return null;
        } finally {
            setSaving(false);
        }
    }, [buildPayload, campaignId]);

    const refreshPreview = useCallback(async (id) => {
        const cid = id || campaignId;
        if (!cid) return;
        setPreviewLoading(true);
        try {
            const res = await axios.get(`/api/campaigns/${cid}/audience/`, { withCredentials: true });
            setPreviewData(res.data);
        } catch { /* best-effort */ } finally {
            setPreviewLoading(false);
        }
    }, [campaignId]);

    useEffect(() => {
        if (step !== 0 || isLocked || !initialized) return;
        if (!isEdit && !setup.title.trim()) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            const id = await saveProgress();
            if (id) refreshPreview(id);
        }, 700);
        return () => clearTimeout(debounceRef.current);
    }, [audience]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (initialized && isEdit && step === 0) refreshPreview();
    }, [initialized]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleExclude = (email) => {
        const excluded = audience.manually_excluded_emails || [];
        setAudience((prev) => ({
            ...prev,
            manually_excluded_emails: excluded.includes(email)
                ? excluded.filter((e) => e !== email)
                : [...excluded, email],
        }));
    };

    const validateStep = () => {
        if (step === 0 && !setup.title.trim()) return 'Campaign name is required.';
        if (step === 1 && !content.subject.trim()) return 'Subject is required.';
        return null;
    };

    const handleNext = async () => {
        const validErr = validateStep();
        if (validErr) { setError(validErr); return; }
        setError(null);
        const id = await saveProgress();
        if (!id) return;
        if (step === 0) refreshPreview(id);
        setEmailPreview((p) => ({ ...p, mode: false }));
        setStep((s) => s + 1);
    };

    const handleBack = () => {
        setError(null);
        setEmailPreview((p) => ({ ...p, mode: false }));
        setStep((s) => s - 1);
    };

    const handleSaveDraft = async () => {
        const id = await saveProgress({ status: 'draft' });
        if (id) {
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 2500);
        }
    };

    const validateQueue = (scheduledFor) => {
        if (!content.subject.trim()) return 'Subject is required.';
        if (!campaignHasAudience(audience)) return AUDIENCE_REQUIRED_MESSAGE;
        if (!scheduledFor) return SCHEDULE_REQUIRED_MESSAGE;
        if (!isScheduleInFuture(scheduledFor)) return SCHEDULE_FUTURE_MESSAGE;
        return null;
    };

    const queueSend = async (scheduledFor) => {
        const validErr = validateQueue(scheduledFor);
        if (validErr) { setError(validErr); return; }
        setError(null);
        const id = await saveProgress({
            status: 'ready',
            scheduled_for: scheduledFor,
        });
        if (!id) return;
        setSaving(true);
        try {
            await axios.post(`/api/campaigns/${id}/send/`, {}, { withCredentials: true });
            navigate(`/league-manager/campaigns/${id}/`);
        } catch (err) {
            setError(formatCampaignSaveError(err, 'Could not queue the campaign send.'));
        } finally {
            setSaving(false);
        }
    };

    const handleMarkReady = () => {
        queueSend(settings.scheduled_for);
    };

    const handleConfirmSendNow = () => {
        const when = scheduleInOneMinute();
        setSettings((prev) => ({ ...prev, scheduled_for: when }));
        setShowSendNow(false);
        queueSend(when);
    };

    const handleShowEmailPreview = async () => {
        const id = await saveProgress();
        if (!id) return;
        setEmailPreview((p) => ({ ...p, loading: true }));
        try {
            const res = await axios.get(`/api/campaigns/${id}/preview-html/`, { withCredentials: true });
            setEmailPreview((p) => ({ ...p, html: res.data.html, mode: true, loading: false }));
        } catch {
            setError('Could not load preview.');
            setEmailPreview((p) => ({ ...p, loading: false }));
        }
    };

    const autoPlainText = content.html_body
        ? content.html_body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
        : '';

    const headerTitle = isEdit ? (setup.title || campaign?.title || 'Campaign') : 'New campaign';
    const scheduleHelpId = 'campaign-schedule-help';
    const scheduleInvalid = step === 2 && settings.scheduled_for && !isScheduleInFuture(settings.scheduled_for);

    return (
        <div className="card">
            <div className="card-header border-0 pb-0">
                {isEdit && (
                    <button
                        type="button"
                        className="btn btn-link btn-sm ps-0 mb-1"
                        onClick={() => navigate(`/league-manager/campaigns/${campaignId}/`)}
                    >
                        ← Back to campaign
                    </button>
                )}
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h5 className="card-title mb-0">{headerTitle}</h5>
                    <CampaignStatusBadge status={settings.status} />
                    <CampaignTypeBadge type={setup.campaign_type} />
                </div>
                {leagueName && <p className="small text-muted mb-0">{leagueName}</p>}
                {isLocked && (
                    <div className="alert alert-warning py-2 mt-2 mb-0 small">
                        This campaign is {settings.status} and cannot be edited.
                    </div>
                )}
            </div>
            <div className="card-body">
                <StepIndicator current={step} />

                {error && <div className="alert alert-danger small py-2 mb-3" role="alert">{error}</div>}

                {step === 0 && (
                    <div>
                        <div className="row g-3 mb-4">
                            <div className="col-md-8">
                                <label className="form-label fw-semibold" htmlFor="campaign-name">
                                    Campaign name <span className="text-danger">*</span>
                                </label>
                                <input
                                    id="campaign-name"
                                    className="form-control"
                                    disabled={isLocked}
                                    placeholder="e.g. Spring season kickoff announcement"
                                    value={setup.title}
                                    onChange={(e) => setSetup({ ...setup, title: e.target.value })}
                                />
                                <div className="form-text">Internal label — not shown to recipients.</div>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold" htmlFor="campaign-type">Type</label>
                                <select
                                    id="campaign-type"
                                    className="form-select"
                                    disabled={isLocked}
                                    value={setup.campaign_type}
                                    onChange={(e) => setSetup({ ...setup, campaign_type: e.target.value })}
                                >
                                    <option value="operational">Operational</option>
                                    <option value="marketing">Marketing</option>
                                </select>
                                <div className="form-text">Marketing requires consent.</div>
                            </div>
                        </div>

                        <div className="row g-4">
                            <div className="col-md-5">
                                <CampaignAudienceSelector
                                    audience={audience}
                                    onChange={setAudience}
                                    leagueId={leagueId}
                                    disabled={isLocked}
                                />
                            </div>
                            <div className="col-md-7" style={{ height: 520 }}>
                                <AudiencePreviewPanel
                                    loading={previewLoading}
                                    summary={previewData?.summary}
                                    included={previewData?.included || []}
                                    excludedEmails={audience.manually_excluded_emails}
                                    onToggleExclude={!isLocked ? toggleExclude : undefined}
                                    noSaveYet={!campaignId}
                                />
                            </div>
                        </div>

                        <details className="mt-4">
                            <summary className="fw-semibold small text-muted" style={{ cursor: 'pointer' }}>
                                Send settings
                            </summary>
                            <div className="mt-3 row g-3 ps-2">
                                <div className="col-md-4">
                                    <label className="form-label small" htmlFor="campaign-batch-size">Batch size</label>
                                    <input
                                        id="campaign-batch-size"
                                        type="number"
                                        className="form-control form-control-sm"
                                        min={1}
                                        disabled={isLocked}
                                        value={settings.batch_size}
                                        onChange={(e) => setSettings({ ...settings, batch_size: Number(e.target.value) })}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small" htmlFor="campaign-rate">Rate / min</label>
                                    <input
                                        id="campaign-rate"
                                        type="number"
                                        className="form-control form-control-sm"
                                        min={1}
                                        disabled={isLocked}
                                        value={settings.send_rate_per_minute}
                                        onChange={(e) => setSettings({ ...settings, send_rate_per_minute: Number(e.target.value) })}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small" htmlFor="campaign-attempts">Max attempts</label>
                                    <input
                                        id="campaign-attempts"
                                        type="number"
                                        className="form-control form-control-sm"
                                        min={1}
                                        disabled={isLocked}
                                        value={settings.max_recipient_attempts}
                                        onChange={(e) => setSettings({ ...settings, max_recipient_attempts: Number(e.target.value) })}
                                    />
                                </div>
                            </div>
                        </details>
                    </div>
                )}

                {step === 1 && (
                    <div>
                        <div className="row g-3 mb-3">
                            <div className="col-md-7">
                                <label className="form-label fw-semibold" htmlFor="campaign-subject">
                                    Subject line <span className="text-danger">*</span>
                                    <span className="text-muted fw-normal ms-2 small">{content.subject.length}/255</span>
                                </label>
                                <input
                                    id="campaign-subject"
                                    className="form-control"
                                    maxLength={255}
                                    disabled={isLocked}
                                    placeholder="What your email is about"
                                    value={content.subject}
                                    onChange={(e) => setContent({ ...content, subject: e.target.value })}
                                />
                            </div>
                            <div className="col-md-5">
                                <label className="form-label fw-semibold" htmlFor="campaign-preheader">
                                    Inbox preview text
                                    <span className="text-muted fw-normal ms-2 small">{content.preheader.length}/255</span>
                                </label>
                                <input
                                    id="campaign-preheader"
                                    className="form-control"
                                    maxLength={255}
                                    disabled={isLocked}
                                    placeholder="The sentence shown below the subject in most inboxes"
                                    value={content.preheader}
                                    onChange={(e) => setContent({ ...content, preheader: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="form-label fw-semibold mb-0">Email body</span>
                                {!isLocked && (
                                    <div className="btn-group btn-group-sm">
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${!emailPreview.mode ? 'btn-secondary' : 'btn-light'}`}
                                            onClick={() => setEmailPreview((p) => ({ ...p, mode: false }))}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${emailPreview.mode ? 'btn-secondary' : 'btn-light'}`}
                                            onClick={handleShowEmailPreview}
                                            disabled={emailPreview.loading || saving}
                                        >
                                            {emailPreview.loading ? 'Loading…' : 'Preview'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {emailPreview.mode ? (
                                <div>
                                    <div className="d-flex gap-2 mb-2">
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${emailPreview.width === 'desktop' ? 'btn-secondary' : 'btn-light'}`}
                                            onClick={() => setEmailPreview((p) => ({ ...p, width: 'desktop' }))}
                                        >
                                            Desktop
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${emailPreview.width === 'mobile' ? 'btn-secondary' : 'btn-light'}`}
                                            onClick={() => setEmailPreview((p) => ({ ...p, width: 'mobile' }))}
                                        >
                                            Mobile
                                        </button>
                                    </div>
                                    <div style={{ border: '1px solid #dee2e6', borderRadius: 6, overflow: 'hidden', background: '#f6f9fc' }}>
                                        <iframe
                                            srcDoc={emailPreview.html}
                                            sandbox="allow-scripts"
                                            style={{
                                                display: 'block',
                                                width: emailPreview.width === 'desktop' ? 600 : 375,
                                                minHeight: 420,
                                                border: 'none',
                                                margin: '0 auto',
                                            }}
                                            title="Email preview"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <RichTextEditor
                                        value={content.html_body}
                                        onChange={(html) => setContent({ ...content, html_body: html })}
                                        disabled={isLocked}
                                    />
                                    <div className="form-text">
                                        For image blocks and advanced layout, edit in{' '}
                                        {campaignId
                                            ? <a href={`/cms/pages/${campaignId}/edit/`} target="_blank" rel="noreferrer">Wagtail CMS</a>
                                            : 'Wagtail CMS after the first save'
                                        }.
                                    </div>
                                </>
                            )}
                        </div>

                        {!emailPreview.mode && (
                            <details>
                                <summary className="small text-muted" style={{ cursor: 'pointer' }}>
                                    Plain text fallback (auto-generated — click to override)
                                </summary>
                                <div className="mt-2">
                                    <label className="visually-hidden" htmlFor="campaign-plain-text">Plain text fallback</label>
                                    <textarea
                                        id="campaign-plain-text"
                                        className="form-control font-monospace"
                                        rows={4}
                                        disabled={isLocked}
                                        placeholder={autoPlainText || 'Auto-derived from body HTML if left blank.'}
                                        value={content.plain_text_custom ? content.plain_text_body : ''}
                                        onFocus={() => setContent((c) => ({ ...c, plain_text_custom: true, plain_text_body: c.plain_text_body || autoPlainText }))}
                                        onChange={(e) => setContent({ ...content, plain_text_body: e.target.value, plain_text_custom: true })}
                                    />
                                    {content.plain_text_custom && (
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm ps-0 mt-1"
                                            onClick={() => setContent({ ...content, plain_text_body: '', plain_text_custom: false })}
                                        >
                                            Reset to auto
                                        </button>
                                    )}
                                </div>
                            </details>
                        )}
                    </div>
                )}

                {step === 2 && (
                    <div>
                        <table className="table table-sm table-borderless mb-3">
                            <tbody>
                                <tr><th className="text-muted fw-normal w-25">Name</th><td>{setup.title}</td></tr>
                                <tr><th className="text-muted fw-normal">Type</th><td className="text-capitalize">{setup.campaign_type}</td></tr>
                                {leagueName && <tr><th className="text-muted fw-normal">League</th><td>{leagueName}</td></tr>}
                                <tr><th className="text-muted fw-normal">Subject</th><td>{content.subject || '—'}</td></tr>
                                <tr><th className="text-muted fw-normal">Preview text</th><td>{content.preheader || '—'}</td></tr>
                                <tr><th className="text-muted fw-normal">From</th><td>{fromEmail || '—'}</td></tr>
                                <tr><th className="text-muted fw-normal">Audience</th><td>{audienceSummaryLabels(audience)}</td></tr>
                                <tr>
                                    <th className="text-muted fw-normal">Recipients</th>
                                    <td>
                                        {recipientNet != null ? <strong>{recipientNet}</strong> : '—'}
                                        {(audience.manually_excluded_emails || []).length > 0 && (
                                            <span className="text-muted ms-2 small">
                                                ({audience.manually_excluded_emails.length} manually excluded)
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="mb-3">
                            <label className="form-label fw-semibold" htmlFor="campaign-schedule-for">
                                Schedule for <span className="text-danger">*</span>
                            </label>
                            <input
                                id="campaign-schedule-for"
                                type="datetime-local"
                                className="form-control"
                                disabled={isLocked}
                                min={toDatetimeLocalValue(new Date())}
                                value={settings.scheduled_for}
                                onChange={(e) => setSettings({ ...settings, scheduled_for: e.target.value })}
                                aria-invalid={scheduleInvalid}
                                aria-describedby={scheduleHelpId}
                            />
                            <div id={scheduleHelpId} className="form-text">
                                Click 'Send now' to start sending or 'Mark Ready' to queue it for sending at a later time.
                            </div>
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-between align-items-center mt-4">
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={handleBack}
                        disabled={step === 0 || saving}
                    >
                        Back
                    </button>
                    <div className="d-flex align-items-center gap-2">
                        {savedFlash && <span className="text-success small">Saved</span>}
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={handleSaveDraft}
                            disabled={saving || isLocked}
                        >
                            {saving ? 'Saving…' : 'Save draft'}
                        </button>
                        {step < STEPS.length - 1 ? (
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={handleNext}
                                disabled={saving || isLocked}
                            >
                                {saving ? 'Saving…' : 'Next'}
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-success btn-sm"
                                    onClick={() => setShowSendNow(true)}
                                    disabled={saving || isLocked}
                                >
                                    Send now
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={handleMarkReady}
                                    disabled={saving || isLocked}
                                >
                                    {saving ? 'Queuing…' : 'Mark ready'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Modal show={showSendNow} onHide={() => setShowSendNow(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title as="h2" className="h5 mb-0">Send now?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">
                        This queues <strong>{setup.title || 'this campaign'}</strong> to start sending in about one minute.
                    </p>
                    <table className="table table-sm table-borderless mb-2">
                        <tbody>
                            <tr>
                                <th className="text-muted fw-normal small">From</th>
                                <td>{fromEmail || '—'}</td>
                            </tr>
                            <tr>
                                <th className="text-muted fw-normal small">Recipients</th>
                                <td className="fw-bold">{recipientNet ?? '—'}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p className="small text-muted mb-0">This cannot be undone once queued.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowSendNow(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="success" size="sm" onClick={handleConfirmSendNow} disabled={saving}>
                        Send
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
